import {
  Component,
  inject,
  signal,
  HostListener,
  ViewChild,
  computed,
} from '@angular/core';
import { Router } from '@angular/router';
import {
  NavDesktopMenuLinkComponent,
  NavDesktopMenuLinkConfig,
  MenuDropdownButtonItem,
  AuthStore,
  PERMISSIONS,
} from '@sandbox-platform/shared-ui';
import { IndustryHierarchyDropdownComponent } from '../../features/industry/hierarchy/industry-hierarchy-dropdown.component';
import {
  DESKTOP_NAVIGATION_LINKS,
  PERSONNEL_DROPDOWN_LINKS,
  REFERRAL_DROPDOWN_LINKS,
  SETUP_DROPDOWN_LINKS,
  PersonnelDropdownData,
  ReferralDropdownData,
  SetupDropdownData,
} from './desktop-menu.config';
import { PersonelDropdownComponent } from './components/personel-dropdown.component';
import { ReferralsDropdownComponent } from './components/referrals-dropdown.component';
import { SetupDropdownComponent } from './components/setup-dropdown.component';

@Component({
  standalone: true,
  imports: [
    NavDesktopMenuLinkComponent,
    PersonelDropdownComponent,
    ReferralsDropdownComponent,
    SetupDropdownComponent,
  ],
  selector: 'lib-expert-desktop-menu',
  templateUrl: './desktop-menu.component.html',
  styles: [
    `
      :host {
        display: block;
      }
    `,
  ],
})
export class ExpertDesktopMenuComponent {
  private router = inject(Router);
  private readonly authStore = inject(AuthStore);

  @ViewChild('industryDropdown')
  industryDropdown?: IndustryHierarchyDropdownComponent;

  isPersonelDropdownOpen = signal(false);
  isSetupDropdownOpen = signal(false);
  isReferralsDropdownOpen = signal(false);

  readonly desktopNavigationItems = computed<NavDesktopMenuLinkConfig[]>(() =>
    DESKTOP_NAVIGATION_LINKS.filter((item) =>
      this.authStore.canAccessExpertRoute(item.routerLink)
    )
  );

  readonly personnelDropdownItems = computed<PersonnelDropdownData>(() => ({
    personnel: PERSONNEL_DROPDOWN_LINKS.personnel.filter((item) =>
      this.authStore.canAccessExpertRoute(item.routerLink)
    ),
    attendance: PERSONNEL_DROPDOWN_LINKS.attendance.filter((item) =>
      this.authStore.canAccessExpertRoute(item.routerLink)
    ),
  }));

  readonly hasPersonnelDropdownItems = computed(() => {
    const menu = this.personnelDropdownItems();
    return menu.personnel.length > 0 || menu.attendance.length > 0;
  });

  readonly referralsDropdownItems = computed<ReferralDropdownData>(() => ({
    referrals: REFERRAL_DROPDOWN_LINKS.referrals.filter((item) =>
      this.authStore.canAccessExpertRoute(item.routerLink)
    ),
    settings: REFERRAL_DROPDOWN_LINKS.settings.filter((item) =>
      this.authStore.canAccessExpertRoute(item.routerLink)
    ),
  }));

  readonly hasReferralsDropdownItems = computed(() => {
    const menu = this.referralsDropdownItems();
    return menu.referrals.length > 0;
  });

  readonly setupDropdownItems = computed<SetupDropdownData>(() => {
    const filterItems = (items: MenuDropdownButtonItem[]) =>
      items.filter((item) =>
        this.authStore.canAccessExpertRoute(item.routerLink)
      );

    return {
      organization: filterItems(SETUP_DROPDOWN_LINKS.organization),
      industry: filterItems(SETUP_DROPDOWN_LINKS.industry),
      attendance: filterItems(SETUP_DROPDOWN_LINKS.attendance),
    };
  });

  readonly hasSetupDropdownItems = computed(() => {
    const menu = this.setupDropdownItems();
    return (
      menu.organization.length > 0 ||
      menu.industry.length > 0 ||
      menu.attendance.length > 0
    );
  });

  readonly canOpenAttendanceTerminal = computed(() =>
    this.authStore.hasExpertPermission(PERMISSIONS.EXPERT.ATTENDANCE_CREATE)
  );

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    const target = event.target as HTMLElement;
    if (!target.closest('.dropdown-container')) {
      this.closeAllDropdowns();
    }
  }

  @HostListener('document:keydown.escape')
  onEscapeKey() {
    this.closeAllDropdowns();
  }

  togglePersonelDropdown(): void {
    this.isSetupDropdownOpen.set(false);
    this.isReferralsDropdownOpen.set(false);
    this.industryDropdown?.closeDropdown();
    this.isPersonelDropdownOpen.update((current) => !current);
  }

  toggleReferralsDropdown(): void {
    this.isSetupDropdownOpen.set(false);
    this.isPersonelDropdownOpen.set(false);
    this.industryDropdown?.closeDropdown();
    this.isReferralsDropdownOpen.update((current) => !current);
  }

  toggleSetupDropdown(): void {
    this.isReferralsDropdownOpen.set(false);
    this.isPersonelDropdownOpen.set(false);
    this.industryDropdown?.closeDropdown();
    this.isSetupDropdownOpen.update((current) => !current);
  }

  closeAllDropdowns(): void {
    this.isPersonelDropdownOpen.set(false);
    this.isSetupDropdownOpen.set(false);
    this.isReferralsDropdownOpen.set(false);
    this.industryDropdown?.closeDropdown();
  }

  navigateToRoute(route: string): void {
    this.closeAllDropdowns();
    this.router.navigate([route]);
  }
}
